import React, { ReactElement, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import styled from "styled-components";

import { Alert, AlertIcon, HStack, Text } from "@chakra-ui/react";
import moment from "moment";
import AppText from "../../components/Core/AppText/AppText";
import Breadcrumbs, { BreadcrumbItem } from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import ConsultantHeader from "../../components/Shared/ConsultantHeader";
import DocumentTab from "../../components/Shared/DocumentTab/DocumentTab";
import FadeIn from "../../components/Shared/FadeIn/FadeIn";
import InvitationModal from "../../components/Shared/InvitationModal";
import SendContractModal from "../../components/Shared/SendContractModal";
import SetPasswordModal from "../../components/Shared/SetPasswordModal";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import SendEmploymentCertificateModal from "../../components/Shared/SendEmploymentCertificateModal/SendEmploymentCertificateModal";
import DetailPageTabs, {
	RouteTabItem,
	ListToolbarAction,
} from "../../components/Shared/DetailPageTabs";
import { ConsultantBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { routes } from "../../configs/constants/routes";
import { FEEDBACK_MESSAGE_TYPE } from "../../configs/feedbackMessages";
import { Documents } from "../../models/Consultant";
import { InviteOption } from "../../models/InternalUser";
import {
	useActivateConsultant,
	useConsultant,
	useDeleteConsultant,
	useInactivateConsultant,
	useRecallConsultantAgreement,
	useSendConsultantContract,
	useToggleConsultantTag,
	useUpdateConsultant,
	useUserLastActivity,
} from "../../networking/consultant.hooks";
import { useInviteUser } from "../../networking/internal-user.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { ConfirmModalProps } from "../../stores/confirmModal.store";
import { formatRoute } from "../../utils/route.utils";
import { generateAvatarString } from "../../utils/string.utils";
import ConsultantAbsenceTab from "./ConsultantAbsenceTab";
import ConsultantAvailabilityTab from "./ConsultantAvailabilityTab";
import ConsultantCustomerBlockTab from "./ConsultantCustomerBlockTab";
import ConsultantHistoryTab from "./ConsultantHistoryTab";
import ConsultantSalaryViewTab from "./ConsultantSalaryViewTab";
import ConsultantsCompetenceTab from "./ConsultantsCompetenceTab";
import ConsultantsEmpAndSalaryTab from "./ConsultantsEmpAndSalaryTab";
import ConsultantShiftsTab from "./ConsultantShiftsTab";
import ConsultantsProfileTab from "./ConsultantsProfileTab";
import TagToggleSubMenu from "../../components/Shared/TagMenu/TagToggleSubMenu";
import {
	removeConsultantTag,
	renewConsultantTag,
	toggleConsultantTag,
} from "../../utils/credential.utils";
import { toast } from "../../utils/toast";
import { useAuthStore } from "../../stores/auth.store";
import { useConfirmModalStore } from "../../stores/confirmModal.store";

interface ConsultantsDetailsParamProps {
	consultantId: string;
}
export interface DetailWrapperStyleProps {
	isHide: boolean;
}

const ConsultantsDetailsPage: React.FC = () => {
	const modalStore = useConfirmModalStore();
	const authStore = useAuthStore();
	const { mutateAsync: inviteUser } = useInviteUser();
	const { consultantId } = useParams<
		keyof ConsultantsDetailsParamProps
	>() as ConsultantsDetailsParamProps;
	const { t } = useTranslation();
	const { data: organisation } = useOrganisation();

	const { consultant, isLoading } = useConsultant({
		consultantId,
		expand: ["noOfJobs", "manager"],
		include: ["ratings"],
	});

	const { lastActivity } = useUserLastActivity(consultantId);
	const { mutate: updateConsultant } = useUpdateConsultant();
	const { mutate: toggleTag } = useToggleConsultantTag();
	const { mutate: activateConsultant } = useActivateConsultant();
	const { mutate: inactivateConsultant } = useInactivateConsultant();
	const { mutate: sendContract } = useSendConsultantContract();
	const { mutate: recallAgreement } = useRecallConsultantAgreement();
	const { mutate: removeConsultant } = useDeleteConsultant();
	const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
	const [isSetPasswordModalOpen, setIsSetPasswordModalOpen] = useState(false);
	const [isSendContractModalOpen, setIsSendContractModalOpen] = useState(false);
	const [isSendEmploymentCertificateModalOpen, setIsSendEmploymentCertificateModalOpen] =
		useState(false);

	const navigate = useNavigate();

	const updateDocuments = async (documents: Documents) => {
		return await updateConsultant({ userId: consultantId, update: { profile: { documents } } });
	};

	const consultantActions: ListToolbarAction[] = [
		{
			label: "RESET_PASSWORD",
			onClick: () => resetPassword(),
		},
		{
			label: "SET_PASSWORD",
			onClick: () => setIsSetPasswordModalOpen(true),
		},
		{
			label: consultant?.active ? "DEACTIVATE" : "ACTIVATE",
			onClick: () => changeActivationConfirm(),
		},
		{
			label: "consultant.invite",
			onClick: () => inviteConsultantConfirm(),
		},
		{
			label: "consultant.sendEmploymentCertificate",
			onClick: () => sendEmploymentCertificate(),
		},
		{
			label: "consultant.sendContract",
			onClick: () => handleSendContract(),
		},
		{
			label: "DELETE_CONSULTANT",
			danger: true,
			dividerBefore: true,
			onClick: () => deleteConsultantConfirm(),
		},
	];

	const resetPassword = async () => {
		if (consultant?.email) {
			const result = await authStore.forgotPassword(consultant.email, organisation?.id);
			if (result) toast.success(FEEDBACK_MESSAGE_TYPE.RESET_PASSWORD_LINK_SENT);
			else toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);
		}
	};

	const changeActivationConfirm = () => {
		const getConsultantWarningText = (): ReactElement => {
			const currentSalaryEndDate = consultant?.profile.employment?.salary
				? moment(consultant?.profile.employment?.salary[0]?.endDate).format("YYYY-MM-DD")
				: moment().format("YYYY-MM-DD");

			let warningText = "";
			if (consultant?.active) {
				warningText = t("DEACTIVATE_CONSULTANT_WARNING").replace(
					"YYYY-MM-DD",
					currentSalaryEndDate
				);
			} else {
				warningText = t("ACTIVATE_CONSULTANT_WARNING").replace("YYYY-MM-DD", currentSalaryEndDate);
			}

			return (
				<>
					<Text>
						{`${
							consultant?.active ? t("DEACTIVATE_CONSULTANT_TEXT") : t("ACTIVATE_CONSULTANT_TEXT")
						}`}
					</Text>
					<Alert status="warning" m={5}>
						<AlertIcon />
						{warningText}
					</Alert>
				</>
			);
		};
		const type = consultant?.active ? "DANGER" : "SUCCESS";
		const changeActivationModal: ConfirmModalProps = {
			title: `${consultant?.active ? "DEACTIVATE_CONSULTANT_TITLE" : "ACTIVATE_CONSULTANT_TITLE"}`,
			text: getConsultantWarningText(),
			acceptButtonText: "YES",
			rejectButtonText: "NO",
			onAccept: () => {
				if (consultant) {
					consultant.active
						? inactivateConsultant({ userId: consultant.id })
						: activateConsultant({ userId: consultant.id });
				}
			},
			onReject: () => {},
			type,
		};
		modalStore.newModal(changeActivationModal);
	};

	const deleteConsultantConfirm = () => {
		const deleteConfirmModal: ConfirmModalProps = {
			title: "DELETE_CONSULTANT",
			text: "DELETE_CONSULTANT_TEXT",
			acceptButtonText: "YES",
			rejectButtonText: "NO",
			onAccept: () => deleteConsultant(),
			onReject: () => {},
			type: "DANGER",
		};
		modalStore.newModal(deleteConfirmModal);
	};

	const inviteConsultantConfirm = () => {
		setIsInviteModalOpen(true);
	};

	const handleSendContract = () => {
		if (
			!consultant?.id ||
			!((consultant.profile.employment?.salary || [])[0] || {}).amount ||
			!consultant.profile.socialSecurityNumber
		) {
			return toast.error(t("consultant.sendContractMissingDetails"));
		}

		setIsSendContractModalOpen(true);
	};

	const sendEmploymentCertificate = async () => {
		setIsSendEmploymentCertificateModalOpen(true);
	};

	const deleteConsultant = () => {
		removeConsultant({ userId: consultantId }, { onSuccess: () => navigate(routes.CONSULTANTS) });
	};

	const inviteConsultant = async (inviteOptionData: InviteOption) => {
		const success = await inviteUser({ userId: consultantId, channels: inviteOptionData })
			.then(() => true)
			.catch(() => false);

		if (success) {
			toast.success(t("inviteModal.success"));
		} else {
			toast.success(t("inviteModal.failure"));
		}
	};

	const navigateToConsultantDetails = () => {
		return (
			formatRoute(routes.CONSULTANTS_DETAILS, [consultantId]) + "/" + routes.CONSULTANTS_PROFILE
		);
	};

	const ConsultantDetailsBreadcrumbs: BreadcrumbItem[] = [
		...ConsultantBreadcrumbs,
		{
			title: (
				<HStack data-contact={consultant?.id}>
					<AppText margin="0 0 0 0.5rem" color="grayText" weight="bold">
						{`/ ${consultant?.firstName} ${consultant?.lastName} (${consultant?.profile?.employeeNumber})`}
					</AppText>
				</HStack>
			),
			path: navigateToConsultantDetails(),
		},
	];

	const handleUploadProfilePicture = useCallback(
		async (pic: any) => {
			if (consultant?.id) {
				await updateConsultant({
					userId: consultant.id,
					update: {
						profile: {
							profilePicture: pic || "",
						},
					},
				});
			}
		},
		[consultant, updateConsultant]
	);

	if (!consultant) return null;

	const consultantTagTypes = organisation?.consultantTagTypes ?? [];

	// Optimistic: the mutation patches this page's ["consultants", consultantId]
	// cache in onMutate, so the header pills and the dropdown checkboxes flip
	// instantly. Rapid toggles compose because each `nextTags` is computed from
	// the already-patched cache.
	const handleToggleTag = (key: string) => {
		const type = consultantTagTypes.find((tagType) => tagType.key === key);
		if (!type) return;

		toggleTag({
			userId: consultant.id,
			nextTags: toggleConsultantTag(consultant.profile?.tags ?? [], type),
		});
	};

	// Re-marking a held timed tag restarts its clock; removal is explicit.
	const handleRenewTag = (key: string) => {
		const type = consultantTagTypes.find((tagType) => tagType.key === key);
		if (!type) return;

		toggleTag({
			userId: consultant.id,
			nextTags: renewConsultantTag(consultant.profile?.tags ?? [], type),
		});
	};

	const handleRemoveTag = (key: string) => {
		toggleTag({
			userId: consultant.id,
			nextTags: removeConsultantTag(consultant.profile?.tags ?? [], key),
		});
	};

	const CONSULTANTS_TAB_GROUPS: RouteTabItem[] = [
		{
			route: routes.CONSULTANTS_PROFILE,
			label: "PROFILE",
			content: <ConsultantsProfileTab consultant={consultant} />,
		},
		{
			route: routes.CONSULTANTS_SHIFTS,
			label: "JOBS",
			content: <ConsultantShiftsTab consultant={consultant} />,
		},
		{
			route: routes.CONSULTANTS_COMPETENCE,
			label: "COMPETENCE",
			content: <ConsultantsCompetenceTab consultant={consultant} />,
		},
		{
			route: routes.CONSULTANTS_EMPLOYMENT_AND_SALARY,
			label: "EMPLOYMENT_AND_SALARY",
			content: <ConsultantsEmpAndSalaryTab consultant={consultant} />,
		},
		{
			route: routes.CONSULTANTS_DOCUMENTS,
			label: "DOCUMENTS",
			content: (
				<DocumentTab
					documents={consultant.profile?.documents}
					updateDocuments={updateDocuments}
					isCandidate={false}
				/>
			),
		},
		{
			route: routes.CONSULTANTS_ABSENCE,
			label: "ABSENCE",
			content: <ConsultantAbsenceTab consultant={consultant} />,
		},
		{
			route: routes.CONSULTANTS_CUSTOMER_BLOCK,
			label: "CUSTOMER_BLOCK",
			content: <ConsultantCustomerBlockTab consultant={consultant} />,
		},
		{
			route: routes.CONSULTANTS_AVAILABILITY,
			label: "AVAILABILITY",
			content: <ConsultantAvailabilityTab consultant={consultant} />,
		},
		{
			route: routes.CONSULTANTS_SALARY_VIEW,
			label: "SALARY_VIEW",
			content: <ConsultantSalaryViewTab consultant={consultant} />,
		},
		{
			route: routes.CONSULTANTS_HISTORY,
			label: "EVENTS",
			content: <ConsultantHistoryTab consultant={consultant} />,
		},
	];

	return (
		<>
			<Breadcrumbs items={ConsultantDetailsBreadcrumbs} />
			<div className="p-4 px-6">
				<FadeIn>
					{isLoading && <LoadingIndicator />}
					{/* This is hide when loading if not the unmounted component will lead to a state update warning */}
					{/* This is related to consultant competence tab state updating */}
					<DetailWrapper isHide={isLoading}>
						<ConsultantHeader
							className="mb-6"
							consultant={consultant}
							lastActivity={lastActivity}
							tagTypes={consultantTagTypes}
							onToggleTag={handleToggleTag}
							onUploadProfilePicture={handleUploadProfilePicture}
						/>
						<DetailPageTabs
							tabs={CONSULTANTS_TAB_GROUPS}
							actions={consultantActions}
							actionsExtraItems={
								consultantTagTypes.length > 0 && (
									<TagToggleSubMenu
										tagTypes={consultantTagTypes}
										assignedTags={consultant.profile?.tags ?? []}
										onToggle={handleToggleTag}
										onRenew={handleRenewTag}
										onRemove={handleRemoveTag}
									/>
								)
							}
						/>
					</DetailWrapper>
					{isInviteModalOpen && (
						<InvitationModal
							title={t("inviteModal.title", { name: consultant?.firstName })}
							acceptButtonText="SEND"
							rejectButtonText="CANCEL"
							onAccept={(inviteOptionData: InviteOption) => {
								inviteConsultant(inviteOptionData);
								setIsInviteModalOpen(false);
							}}
							onReject={() => setIsInviteModalOpen(false)}
							text={t("inviteModal.text")}
							email={consultant?.email}
							phoneNo={consultant.profile?.phoneNumber}
						/>
					)}
					{isSendContractModalOpen && (
						<SendContractModal
							title={t("consultant.sendContractTitle", { name: consultant?.firstName })}
							acceptButtonText="SEND"
							rejectButtonText="CANCEL"
							onAccept={(inviteOptionData: InviteOption, expireDate: string) => {
								if (consultant) {
									sendContract({
										userId: consultant.id,
										sms: inviteOptionData.sms,
										mail: true,
										expireDate: expireDate,
									});
								}
								setIsSendContractModalOpen(false);
							}}
							onReject={() => setIsSendContractModalOpen(false)}
							text={t("consultant.sendContractText")}
							email={consultant?.email}
							phoneNo={consultant.profile?.phoneNumber}
						/>
					)}
					<SendEmploymentCertificateModal
						isOpen={isSendEmploymentCertificateModalOpen}
						onClose={() => setIsSendEmploymentCertificateModalOpen(false)}
						consultantId={consultantId}
					/>
					<SetPasswordModal
						isOpen={isSetPasswordModalOpen}
						onClose={() => setIsSetPasswordModalOpen(false)}
						userId={consultantId}
					/>
				</FadeIn>
			</div>
		</>
	);
};

export default ConsultantsDetailsPage;

const DetailWrapper = styled.div<DetailWrapperStyleProps>`
	display: ${(props) => (props.isHide ? "none" : "block")};
	padding-bottom: 4rem;
`;
